/**
 * My Disputes Page - View, create, and respond to disputes
 * For customers, drivers, merchants, and PUDO operators
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Link2,
  ArrowLeft,
  AlertTriangle,
  MessageSquare,
  FileText,
  Upload,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  Shield,
  Loader2,
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  Paperclip
} from 'lucide-react';

const STATUS_COLORS = {
  open: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  under_review: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  resolved: 'bg-green-500/20 text-green-400 border-green-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
  escalated: 'bg-purple-500/20 text-purple-400 border-purple-500/30'
};

const SEVERITY_COLORS = {
  low: 'bg-slate-500/20 text-slate-400',
  medium: 'bg-yellow-500/20 text-yellow-400',
  high: 'bg-orange-500/20 text-orange-400',
  critical: 'bg-red-500/20 text-red-400'
};

const MyDisputes = () => {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedDispute, setExpandedDispute] = useState(null);
  const [disputeDetails, setDisputeDetails] = useState({});
  
  // Response form state
  const [responseText, setResponseText] = useState('');
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);

  // Determine user role for display
  const isDriver = hasRole && hasRole('DRIVER');
  const isMerchant = hasRole && hasRole('BUSINESS_MERCHANT');
  const isPudo = hasRole && hasRole('PUDO_POINT');
  const canRespond = isDriver || isMerchant || isPudo;

  useEffect(() => {
    fetchDisputes();
  }, [statusFilter]);

  const fetchDisputes = async () => {
    setLoading(true);
    try {
      // Fetch disputes involving the user
      const params = statusFilter ? `?status_filter=${statusFilter}` : '';
      const response = await api.get(`/dispute-responses/disputes/involving-me${params}`);
      setDisputes(response.data.disputes || []);
    } catch (error) {
      console.error('Failed to fetch disputes:', error);
      // Fallback to my disputes
      try {
        const fallback = await api.get('/disputes/my');
        setDisputes(fallback.data.disputes || []);
      } catch (err) {
        toast.error('Failed to load disputes');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDisputeDetails = async (disputeId) => {
    if (disputeDetails[disputeId]) return;
    
    try {
      const response = await api.get(`/dispute-responses/disputes/${disputeId}`);
      setDisputeDetails(prev => ({
        ...prev,
        [disputeId]: response.data
      }));
    } catch (error) {
      console.error('Failed to fetch dispute details:', error);
    }
  };

  const toggleExpand = async (disputeId) => {
    if (expandedDispute === disputeId) {
      setExpandedDispute(null);
    } else {
      setExpandedDispute(disputeId);
      await fetchDisputeDetails(disputeId);
    }
    setResponseText('');
    setEvidenceFiles([]);
  };

  const handleFileUpload = async (disputeId, file) => {
    setUploadingEvidence(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(
        `/dispute-responses/disputes/${disputeId}/evidence`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setEvidenceFiles(prev => [...prev, response.data.evidence_url]);
      toast.success('Evidence uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload evidence');
    } finally {
      setUploadingEvidence(false);
    }
  };

  const handleSubmitResponse = async (disputeId) => {
    if (!responseText || responseText.length < 20) {
      toast.error('Response must be at least 20 characters');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/dispute-responses/disputes/${disputeId}/respond`, {
        response_text: responseText,
        evidence_urls: evidenceFiles
      });
      
      toast.success('Response submitted successfully');
      setResponseText('');
      setEvidenceFiles([]);
      
      // Refresh dispute details
      const response = await api.get(`/dispute-responses/disputes/${disputeId}`);
      setDisputeDetails(prev => ({
        ...prev,
        [disputeId]: response.data
      }));
      
      // Refresh disputes list
      fetchDisputes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <Link2 className="h-7 w-7 text-primary" />
              <span className="text-xl font-bold tracking-tight">Liantiao</span>
              <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-1 rounded border border-border">
                MY DISPUTES
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Info Banner for Drivers/Merchants */}
        {canRespond && (
          <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-blue-400 mt-0.5" />
              <div>
                <p className="text-sm text-blue-400 font-medium">Dispute Response Available</p>
                <p className="text-sm text-muted-foreground mt-1">
                  As a {isDriver ? 'driver' : isMerchant ? 'merchant' : 'PUDO operator'}, you can respond to disputes 
                  filed against your deliveries with evidence. Your response will be reviewed by admin before a decision is made.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Status Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { value: '', label: 'All' },
            { value: 'open', label: 'Open' },
            { value: 'under_review', label: 'Under Review' },
            { value: 'resolved', label: 'Resolved' },
            { value: 'rejected', label: 'Rejected' }
          ].map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setStatusFilter(value)}
              className={`px-3 py-1.5 text-sm rounded-lg border transition-all ${
                statusFilter === value
                  ? 'bg-primary/10 border-primary text-primary'
                  : 'bg-card border-border text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`filter-${value || 'all'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Disputes List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : disputes.length === 0 ? (
          <div className="text-center py-20">
            <AlertTriangle className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Disputes</h3>
            <p className="text-muted-foreground">
              {statusFilter ? `No ${statusFilter.replace('_', ' ')} disputes found.` : 'You have no disputes.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {disputes.map((dispute) => {
              const isExpanded = expandedDispute === dispute.id;
              const details = disputeDetails[dispute.id];
              const isRaiser = dispute.raised_by_user_id === user?.id;
              
              return (
                <div
                  key={dispute.id}
                  className="bg-card border border-border rounded-lg overflow-hidden"
                  data-testid={`dispute-${dispute.id}`}
                >
                  {/* Dispute Header */}
                  <button
                    onClick={() => toggleExpand(dispute.id)}
                    className="w-full p-5 text-left hover:bg-muted/30 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-2 py-0.5 text-xs rounded border ${STATUS_COLORS[dispute.status]}`}>
                            {dispute.status?.replace('_', ' ')}
                          </span>
                          <span className={`px-2 py-0.5 text-xs rounded ${SEVERITY_COLORS[dispute.severity]}`}>
                            {dispute.severity}
                          </span>
                          {isRaiser ? (
                            <span className="text-xs text-muted-foreground">You raised this</span>
                          ) : (
                            <span className="text-xs text-orange-400">Action needed</span>
                          )}
                        </div>
                        
                        <h3 className="font-medium text-foreground mb-1">
                          {dispute.reason?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </h3>
                        
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {dispute.description}
                        </p>
                        
                        <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(dispute.created_at).toLocaleDateString()}
                          </span>
                          <span>Package: {dispute.package_id?.slice(0, 8)}...</span>
                          {!isRaiser && (
                            <span>By: {dispute.raised_by_name}</span>
                          )}
                        </div>
                      </div>
                      
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                  </button>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-border p-5">
                      {/* Full Description */}
                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-muted-foreground mb-2">Description</h4>
                        <p className="text-foreground">{dispute.description}</p>
                      </div>

                      {/* Attachments */}
                      {dispute.attachments && dispute.attachments.length > 0 && (
                        <div className="mb-6">
                          <h4 className="text-sm font-medium text-muted-foreground mb-2">Attachments</h4>
                          <div className="flex gap-2">
                            {dispute.attachments.map((url, idx) => (
                              <a
                                key={idx}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-16 h-16 rounded-lg border border-border overflow-hidden hover:border-primary transition-colors"
                              >
                                <img src={url} alt={`Attachment ${idx + 1}`} className="w-full h-full object-cover" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Responses */}
                      {details?.responses && details.responses.length > 0 && (
                        <div className="mb-6">
                          <h4 className="text-sm font-medium text-muted-foreground mb-3">Responses</h4>
                          <div className="space-y-3">
                            {details.responses.map((response, idx) => (
                              <div
                                key={idx}
                                className="p-4 bg-muted/30 rounded-lg border-l-2 border-primary"
                              >
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-sm font-medium text-foreground">
                                    {response.responder_name}
                                  </span>
                                  <span className="text-xs px-2 py-0.5 rounded bg-primary/20 text-primary capitalize">
                                    {response.responder_role}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {new Date(response.created_at).toLocaleString()}
                                  </span>
                                </div>
                                <p className="text-sm text-foreground">{response.response_text}</p>
                                
                                {response.evidence_urls && response.evidence_urls.length > 0 && (
                                  <div className="flex gap-2 mt-3">
                                    {response.evidence_urls.map((url, i) => (
                                      <a
                                        key={i}
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-12 h-12 rounded border border-border overflow-hidden"
                                      >
                                        <img src={url} alt="Evidence" className="w-full h-full object-cover" />
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Resolution */}
                      {dispute.resolution && (
                        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                          <h4 className="text-sm font-medium text-green-400 mb-2">Resolution</h4>
                          <p className="text-sm text-foreground">{dispute.resolution.notes}</p>
                          {dispute.resolution.refund_amount > 0 && (
                            <p className="text-sm text-muted-foreground mt-2">
                              Refund: J${dispute.resolution.refund_amount}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Response Form - Only for involved parties who haven't responded */}
                      {details?.can_respond && !details?.already_responded && !isRaiser && (
                        <div className="mt-6 p-4 bg-muted/30 rounded-lg border border-border">
                          <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                            <MessageSquare className="h-4 w-4 text-primary" />
                            Submit Your Response
                          </h4>
                          
                          <textarea
                            value={responseText}
                            onChange={(e) => setResponseText(e.target.value)}
                            placeholder="Provide your response and explanation (minimum 20 characters)..."
                            className="w-full px-3 py-2 bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none mb-3"
                            rows={4}
                            data-testid="response-input"
                          />
                          
                          {/* Evidence Upload */}
                          <div className="mb-4">
                            <label className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                              <Paperclip className="h-4 w-4" />
                              Attach Evidence (optional)
                            </label>
                            
                            {evidenceFiles.length > 0 && (
                              <div className="flex gap-2 mb-2">
                                {evidenceFiles.map((url, idx) => (
                                  <div key={idx} className="relative">
                                    <img
                                      src={url}
                                      alt={`Evidence ${idx + 1}`}
                                      className="w-12 h-12 rounded border border-border object-cover"
                                    />
                                    <button
                                      onClick={() => setEvidenceFiles(prev => prev.filter((_, i) => i !== idx))}
                                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-xs flex items-center justify-center"
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            <label className="inline-flex items-center gap-2 px-3 py-1.5 bg-muted border border-border rounded-lg cursor-pointer hover:bg-muted/80 transition-colors">
                              {uploadingEvidence ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Upload className="h-4 w-4" />
                              )}
                              <span className="text-sm">Upload File</span>
                              <input
                                type="file"
                                accept="image/*,application/pdf"
                                onChange={(e) => {
                                  if (e.target.files?.[0]) {
                                    handleFileUpload(dispute.id, e.target.files[0]);
                                  }
                                }}
                                className="hidden"
                                disabled={uploadingEvidence}
                              />
                            </label>
                          </div>
                          
                          <Button
                            onClick={() => handleSubmitResponse(dispute.id)}
                            disabled={submitting || responseText.length < 20}
                            className="bg-primary text-primary-foreground hover:bg-primary/90"
                            data-testid="submit-response-btn"
                          >
                            {submitting ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <Send className="h-4 w-4 mr-2" />
                            )}
                            Submit Response
                          </Button>
                        </div>
                      )}

                      {/* Already Responded Notice */}
                      {details?.already_responded && (
                        <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                          <CheckCircle className="h-5 w-5 text-green-400 mx-auto mb-1" />
                          <p className="text-sm text-green-400">You have already responded to this dispute</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default MyDisputes;
